"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Plus, Edit, Trash2, Trophy, Search, Calendar, MapPin, Users, ChevronRight } from "lucide-react"
import type { Competition, AgeCategory } from "../types"
import { api } from "../services/api"

const AGE_CATEGORIES: AgeCategory[] = ["Adult", "U23", "U20-Junior", "U17-Cadet", "U15", "U14", "U13", "U11", "U9"]

interface CompetitionFormData {
  name: string
  date: string
  location: string
  ageCategories: AgeCategory[]
  notes: string
}

export const CompetitionsPage: React.FC = () => {
  const navigate = useNavigate()
  const [competitions, setCompetitions] = useState<Competition[]>([])
  const [filteredCompetitions, setFilteredCompetitions] = useState<Competition[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [showForm, setShowForm] = useState(false)
  const [editingCompetition, setEditingCompetition] = useState<Competition | null>(null)
  const [loading, setLoading] = useState(true)
  const [formData, setFormData] = useState<CompetitionFormData>({
    name: "",
    date: "",
    location: "",
    ageCategories: ["Adult"],
    notes: "",
  })

  useEffect(() => {
    loadCompetitions()
  }, [])

  useEffect(() => {
    // Filter competitions based on search term
    const filtered = competitions.filter((competition) => {
      return (
        competition.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        competition.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        competition.ageCategories.some((cat) => cat.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    })
    setFilteredCompetitions(filtered)
  }, [competitions, searchTerm])

  const loadCompetitions = async () => {
    try {
      const data = await api.getCompetitions()
      // Sort by date, newest first
      data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      setCompetitions(data)
    } catch (error) {
      console.error("Error loading competitions:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      if (editingCompetition) {
        await api.updateCompetition(editingCompetition.id, formData)
      } else {
        await api.addCompetition(formData)
      }

      await loadCompetitions()
      resetForm()
    } catch (error) {
      console.error("Error saving competition:", error)
    }
  }

  const handleEdit = (competition: Competition) => {
    setEditingCompetition(competition)
    setFormData({
      name: competition.name,
      date: competition.date,
      location: competition.location,
      ageCategories: competition.ageCategories,
      notes: competition.notes || "",
    })
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this competition? This will also delete all related results.")) return

    try {
      await api.deleteCompetition(id)
      await loadCompetitions()
    } catch (error) {
      console.error("Error deleting competition:", error)
    }
  }

  const resetForm = () => {
    setFormData({
      name: "",
      date: "",
      location: "",
      ageCategories: ["Adult"],
      notes: "",
    })
    setEditingCompetition(null)
    setShowForm(false)
  }

  const handleAgeCategoryChange = (category: AgeCategory) => {
    setFormData((prev) => {
      const newCategories = prev.ageCategories.includes(category)
        ? prev.ageCategories.filter((c) => c !== category)
        : [...prev.ageCategories, category]

      return {
        ...prev,
        ageCategories: newCategories.length > 0 ? newCategories : prev.ageCategories, // Ensure at least one category
      }
    })
  }

  const handleViewCompetition = (id: string) => {
    navigate(`/competitions/${id}`)
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading competitions...</span>
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Competitions</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            Manage competitions and track fencer participation
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:shadow-md"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Competition
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mt-6 card p-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          </div>
          <input
            type="text"
            placeholder="Search competitions by name, location, or age category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-500 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
          />
        </div>
      </div>

      {/* Competition Form */}
      {showForm && (
        <div className="mt-8 card p-6 animate-slide-down">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {editingCompetition ? "Edit Competition" : "Add New Competition"}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Competition Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  required
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-2 transition-all duration-200"
                  placeholder="Enter competition name"
                />
              </div>

              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Competition Date *
                </label>
                <input
                  type="date"
                  id="date"
                  value={formData.date}
                  onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
                  required
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-2 transition-all duration-200"
                />
              </div>
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Location *
              </label>
              <input
                type="text"
                id="location"
                value={formData.location}
                onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
                required
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-2 transition-all duration-200"
                placeholder="Enter competition location"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Age Categories *
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {AGE_CATEGORIES.map((category) => (
                  <div key={category} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`category-${category}`}
                      checked={formData.ageCategories.includes(category)}
                      onChange={() => handleAgeCategoryChange(category)}
                      className="h-4 w-4 text-blue-600 dark:text-blue-500 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <label
                      htmlFor={`category-${category}`}
                      className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                    >
                      {category}
                    </label>
                  </div>
                ))}
              </div>
              {formData.ageCategories.length === 0 && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">Please select at least one age category</p>
              )}
            </div>

            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notes
              </label>
              <textarea
                id="notes"
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-2 transition-all duration-200"
                placeholder="Add any additional notes about the competition..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 hover:shadow-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 border border-transparent rounded-md transition-all duration-200 hover:shadow-md"
              >
                {editingCompetition ? "Update" : "Add"} Competition
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Competitions List */}
      <div className="mt-8 space-y-4 animate-slide-up">
        {filteredCompetitions.length > 0 ? (
          filteredCompetitions.map((competition) => (
            <div
              key={competition.id}
              className="card p-4 hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => handleViewCompetition(competition.id)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">{competition.name}</h3>
                  <div className="mt-1 flex flex-wrap items-center text-sm text-gray-500 dark:text-gray-400 space-x-4">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{competition.date}</span>
                    </div>
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      <span>{competition.location}</span>
                    </div>
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      <span>{competition.ageCategories.join(", ")}</span>
                    </div>
                  </div>
                  {competition.notes && (
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{competition.notes}</p>
                  )}
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEdit(competition)
                    }}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1 rounded transition-colors duration-200"
                    title="Edit competition"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(competition.id)
                    }}
                    className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded transition-colors duration-200"
                    title="Delete competition"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12 card">
            <Trophy className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No competitions found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm ? "Try adjusting your search terms." : "Get started by adding your first competition."}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowForm(true)}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Competition
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
